import React, { useEffect, useState } from 'react';
import DataTable from '../../components/UI/table';
import { styles } from './index.sass';
import { Button, Modal, Form, Input, InputNumber, DatePicker, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { CheckCircleTwoTone, ClockCircleTwoTone } from '@ant-design/icons';
import { createContractorSession, getContractorSessions } from '../../api/contractorSession';
import { getClients } from '../../api';
import moment from 'moment';

export const ContractorSessions = () => {
    const [loading, setLoading] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [clients, setClients] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);
    const [selectedDate, setSelectedDate] = useState({ start: null, end: null });
    const [selectedContractor, setSelectedContractor] = useState(null);

    const columns = [
        {
            title: 'Status', key: 'is_approved', render: (record) => {
                const matched = record.appointment_id ? true : false;
                let is_partial_matched = false;
                if (matched) {
                    const original_appointment = JSON.parse(record.original_appointment);
                    is_partial_matched = !(original_appointment.duration === record.duration);
                }
                return (
                    (record.appointment_id && !is_partial_matched) ?
                        <CheckCircleTwoTone twoToneColor="#52c41a" title="Matched" /> :
                        (is_partial_matched ? <ClockCircleTwoTone twoToneColor="#faad14" title="Partially Matched" /> : <ClockCircleTwoTone twoToneColor="red" title="Unmatched" />)
                )
            }
        },
        { title: 'Contractor Name', dataIndex: 'contractor_name', key: 'contractor_name' },
        {
            title: 'Client', dataIndex: 'client_id', key: 'client_id', render: (person_id) => {
                console.log('person_id', person_id, clients);
                return clients.find(client => client.id === person_id).full_name || "";
            }
        },
        { title: 'Duration', dataIndex: 'duration', key: 'duration' },
        { title: 'Date', dataIndex: 'date', key: 'date' },
        { title: 'Pay', dataIndex: 'pay', key: 'pay' },
        { title: 'Original Appointment', dataIndex: 'original_appointment', key: 'original_appointment' },
    ];

    const fetchContractorSessions = async () => {
        setLoading(true);
        const sessions = await getContractorSessions({
            contractor_name: selectedContractor,
            start_date: selectedDate.start ? selectedDate.start.format('YYYY-MM-DD') : undefined,
            end_date: selectedDate.end ? selectedDate.end.format('YYYY-MM-DD') : undefined
        });
        if (clients.length === 0) {
            const clientsData = await getClients();
            setClients(clientsData.clients);
        }
        setSessions(sessions);
        setLoading(false);
    }

    const onClickCreateContractorSession = async (data) => {
        setLoading(true);
        await createContractorSession(data);
        setLoading(false);
        setSelectedSession(null);
        fetchContractorSessions();
    }

    useEffect(() => {
        fetchContractorSessions();
    }, []);

    useEffect(() => {
        fetchContractorSessions()
    }, [selectedDate, selectedContractor]);

    const handleChangeDate = (dates) => {
        setSelectedDate({
            start: dates ? dates[0] : null,
            end: dates ? dates[1] : null
        });
    }

    return (
        <div className={styles}>
            <div className='container'>
                <div className='flex'>
                    <h2>Contractor Sessions</h2>
                    <div>
                        <Button type="default" onClick={() => setSelectedSession({})}>
                            <PlusOutlined />
                        </Button>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 16, margin: '16px 0' }}>
                    <DatePicker.RangePicker
                        placeholder={['Start Date', 'End Date']}
                        value={[
                            selectedDate.start ? moment(selectedDate.start) : null,
                            selectedDate.end ? moment(selectedDate.end) : null
                        ]}
                        onChange={handleChangeDate}
                    />
                    <Select
                        showSearch
                        allowClear
                        placeholder="Filter by Contractor Name"
                        style={{ width: 220 }}
                        optionFilterProp="children"
                        value={selectedContractor || undefined}
                        onChange={value => setSelectedContractor(value)}
                        filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        {[...new Set((sessions || []).map(s => s.contractor_name))].map(name => (
                            <Select.Option key={name} value={name}>{name}</Select.Option>
                        ))}
                    </Select>
                </div>
                <div>
                    <DataTable
                        columns={columns || []}
                        dataSource={sessions || []}
                        loading={loading}
                        hidePagination={true}
                    />
                </div>
                <Modal
                    open={selectedSession !== null}
                    onCancel={() => setSelectedSession(null)}
                    footer={null}
                >
                    <div>
                        <div style={{ fontWeight: 600, marginBottom: 12 }}>Create Contractor Session</div>
                        <Form
                            layout="vertical"
                            initialValues={{
                                contractor_name: selectedSession && selectedSession.contractor_name ? selectedSession.contractor_name : '',
                                client_id: selectedSession && selectedSession.client_id ? selectedSession.client_id : '',
                                duration: selectedSession && selectedSession.duration ? Number(selectedSession.duration) : '',
                                date: selectedSession && selectedSession.date ? selectedSession.date : null,
                                pay: selectedSession && selectedSession.pay ? Number(selectedSession.pay) : '',
                            }}
                            onFinish={(data) => onClickCreateContractorSession(data)}
                        >
                            <Form.Item label="Contractor Name" name="contractor_name" rules={[{ required: true, message: 'Please enter contractor name' }]}>
                                <Input
                                    value={selectedSession && selectedSession.contractor_name ? selectedSession.contractor_name : ''}
                                    onChange={e => setSelectedSession(Object.assign({}, selectedSession, { contractor_name: e.target.value }))}
                                />
                            </Form.Item>
                            <Form.Item label="Client" name="client_id" rules={[{ required: true, message: 'Please select a person' }]}>
                                <Select
                                    showSearch
                                    placeholder="Select a client"
                                    optionFilterProp="children"
                                    value={selectedSession && selectedSession.client_id ? selectedSession.client_id : undefined}
                                    onChange={value => setSelectedSession(Object.assign({}, selectedSession, { client_id: value }))}
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {(clients || []).map(client => (
                                        <Select.Option key={client.id} value={client.id}>
                                            {client.full_name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item label="Duration (minutes)" name="duration" rules={[{ required: true, message: 'Please enter duration' }]}>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    value={selectedSession && selectedSession.duration ? Number(selectedSession.duration) : ''}
                                    onChange={value => setSelectedSession(Object.assign({}, selectedSession, { duration: value }))}
                                />
                            </Form.Item>
                            <Form.Item label="Date" name="date" rules={[{ required: true, message: 'Please select date' }]}>
                                <DatePicker
                                    style={{ width: '100%' }}
                                    value={selectedSession && selectedSession.date ? selectedSession.date : null}
                                    onChange={(_, dateString) => setSelectedSession(Object.assign({}, selectedSession, { date: dateString }))}
                                />
                            </Form.Item>
                            <Form.Item label="Pay ($)" name="pay" rules={[{ required: true, message: 'Please enter pay' }]}>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    value={selectedSession && selectedSession.pay ? Number(selectedSession.pay) : ''}
                                    onChange={value => setSelectedSession(Object.assign({}, selectedSession, { pay: value }))}
                                />
                            </Form.Item>
                            <Form.Item>
                                <Button type="primary" htmlType="submit" loading={loading}>
                                    Submit
                                </Button>
                            </Form.Item>
                        </Form>
                    </div>
                </Modal>
            </div>
        </div >
    )
}